"use client";

import { Button } from "@universe/ui/components/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandList,
} from "@universe/ui/components/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@universe/ui/components/popover";
import { Skeleton } from "@universe/ui/components/skeleton";
import { cn } from "@universe/ui/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronsUpDown } from "lucide-react";
import { useTranslations } from "next-intl";
import React from "react";

import CurrencyFlag from "../currency-flag";
import { NoTranslate } from "../no-translate";

type AccountSelectPopoverProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
  className?: string;
  popoverAlign?: "start" | "center" | "end";
  // Trigger props
  selectedValue?: string;
  selectedLabel?: string;
  /** Display name for the selected item (shown in regular font) */
  selectedName?: string;
  showFlag?: boolean;
  flagSize?: string;
  placeholder?: string;
  fixedCurrency?: boolean;
  /** When true, only show the currency code (not the name) in the trigger */
  showCodeOnly?: boolean;
  /** When true, only show the name (not the currency code) in the trigger */
  showNameOnly?: boolean;
  // Custom trigger
  customTriggerText?: string;
  customTriggerClassName?: string;
  // Content
  children: React.ReactNode;
  emptyMessage?: string;
  /** Placeholder for the search input inside the popover */
  searchPlaceholder?: string;
};

export function AccountSelectPopover({
  open,
  onOpenChange,
  isLoading = false,
  className,
  popoverAlign = "start",
  selectedValue,
  selectedLabel,
  selectedName,
  showFlag = false,
  flagSize = "28px",
  placeholder,
  fixedCurrency = false,
  showCodeOnly = false,
  showNameOnly = false,
  customTriggerText,
  customTriggerClassName,
  children,
  emptyMessage,
  searchPlaceholder,
}: AccountSelectPopoverProps) {
  const tAccounts = useTranslations("accounts");

  if (isLoading) {
    return (
      <div className="flex gap-2 flex-col lg:flex-row">
        <Skeleton className="w-full h-10" />
      </div>
    );
  }

  // Render the selected account display similar to CountrySelect
  const renderSelectedDisplay = () => {
    if (!selectedValue) {
      return (
        <span className="text-muted-foreground">
          {placeholder || tAccounts("selectAccount")}
        </span>
      );
    }

    // If showCodeOnly is true, only show the currency code
    if (showCodeOnly) {
      return (
        <NoTranslate className="font-semibold">{selectedValue}</NoTranslate>
      );
    }

    // If showNameOnly is true, only show the name. Name comes from a
    // backend-supplied string (e.g. "British Pound") which browser
    // translation will rewrite ("Sterlina inglese") — wrap in NoTranslate
    // to keep it stable. Most callers should prefer the default
    // (code-only) display.
    if (showNameOnly && selectedName) {
      return (
        <NoTranslate className="font-semibold">{selectedName}</NoTranslate>
      );
    }

    // If we have a name, show ONLY the ISO currency code. Previously
    // rendered "Name - CODE" but the localized name (e.g. Italian
    // "Sterlina inglese" for GBP) was both translation-fragile and
    // redundant alongside the ISO code. Standardised on code-only here
    // so currency dropdowns show "GBP" / "EUR" / "USD" / "AED".
    if (selectedName) {
      return (
        <NoTranslate className="font-semibold">{selectedValue}</NoTranslate>
      );
    }

    // No name — fall back to caller-supplied label (e.g. a formatted
    // balance like "1,234.56 GBP") or the bare ISO code. Either way
    // wrap in NoTranslate so the rendered string isn't rewritten.
    if (selectedLabel) {
      return <NoTranslate>{selectedLabel}</NoTranslate>;
    }
    return <NoTranslate>{selectedValue}</NoTranslate>;
  };

  return (
    <div className="flex gap-2 flex-col lg:flex-row">
      <Popover
        open={open}
        onOpenChange={fixedCurrency ? undefined : onOpenChange}
        modal={true}
      >
        <PopoverTrigger asChild>
          <Button
            variant={customTriggerText ? "secondary" : "ghost"}
            role="combobox"
            aria-expanded={open}
            size="default"
            className={cn(
              customTriggerText
                ? customTriggerClassName
                : cn(
                    "w-vw w-full justify-between text-sm md:text-base font-base px-3 hover:bg-brand-primary/5",
                    className,
                    fixedCurrency &&
                      "hover:bg-transparent hover:cursor-not-allowed"
                  )
            )}
          >
            {customTriggerText ? (
              customTriggerText
            ) : (
              <>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedValue || "placeholder"}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center gap-2"
                  >
                    {selectedValue && showFlag && (
                      <div>
                        <CurrencyFlag
                          currency={selectedValue}
                          size={flagSize}
                        />
                      </div>
                    )}
                    {renderSelectedDisplay()}
                  </motion.div>
                </AnimatePresence>
                {!fixedCurrency && (
                  <ChevronsUpDown className="ml-0 h-4 w-4 shrink-0 text-black" />
                )}
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="!w-full p-0 z-[9999] pointer-events-auto isolate border-none"
          side="bottom"
          align={popoverAlign}
          sideOffset={8}
          avoidCollisions={true}
          collisionPadding={20}
          style={{
            isolation: "isolate",
            contain: "layout style",
          }}
          onOpenAutoFocus={(e: Event) => e.preventDefault()}
        >
          <Command className="w-full max-w-[90vw] w-[350px] lg:w-[380px] flex flex-col shadow-lg border-r border-b border-t border-l">
            <CommandInput
              placeholder={
                searchPlaceholder || tAccounts("selectAccountPlaceholder")
              }
              className="py-2 text-base font-base flex-shrink-0"
            />
            <div
              className="overflow-hidden flex-1 min-h-0"
              data-scroll-container="true"
            >
              <CommandList
                className="h-full overflow-y-auto overscroll-contain [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-brand-primary/20 [&::-webkit-scrollbar-thumb]:rounded-full"
                style={{
                  maxHeight: "min(400px, calc(80vh - 120px))",
                  overflowY: "auto" as const,
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "thin",
                  position: "relative",
                  display: "block",
                  borderRadius: "12px",
                }}
                onWheel={(e) => {
                  e.stopPropagation();
                }}
              >
                <CommandEmpty>
                  {emptyMessage || tAccounts("noAccounts")}
                </CommandEmpty>
                {children}
              </CommandList>
            </div>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
